// ─── React & Zustand ─────────────────────────────────────────────
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import { createStore, useStore, StoreApi } from "zustand";
import { useLocation } from "react-router-dom";

// ─── API Services ────────────────────────────────────────────────
import {
  getOrganizationWalletAmounts,
  getOrganizationWalletGroupingHistory,
  createOrganizationWithdrawalApi,
  getOrganizationWithdrawalHistory,
  getTotalOrganizationWalletAmounts,
  getTotalOrganizationWalletGroupingHistory,
} from "../../setup/api/services/wallet/index";

// ─── Context & Providers ──────────────────────────────────────────
import { useAlertStore } from "../../store/alertStore";
import { useTranslation } from "react-i18next";
import { useOrganizationCode } from "../../setup/util/extractOrganizationCode";
import { AuthConfig } from "../../setup/context/config/authConfig";
import { ROUTES } from "../../setup/constants";
import {
  TransactionHistory,
  TransactionStatus,
} from "../../pages/business/balance/types";

// ─── Types & Interfaces ──────────────────────────────────────────
interface Transaction {
  domainId: string;
  title: string | null;
  status: TransactionStatus;
  walletHistories: TransactionHistory[];
  cashedOut: boolean;
  partyCashOut?: {
    amountDue: number;
    cashOutAmount: number;
    fee: number;
    feePercentage: number;
    id: number;
  };
}

interface WithdrawalHistoryItem {
  // Add specific fields as needed
  referenceNumber: string;
  toAccount: string;
  withdrawalNumber: string;
  amount: number;
  histories: Array<{
    date: string;
    status: string;
  }>;
}

interface WalletAmounts {
  cashOutAmount: number;
  openedAmount: number;
  availableBalanceAmount: number;
  inProgressAmount: number;
}

interface PaginatedResponse<T> {
  content: T[];
  totalPages: number;
  pageable?: {
    pageNumber: number;
    [key: string]: any;
  };
  page?: number;
  [key: string]: any;
}

type TabType = "transaction" | "bulk";

// ─── State Interfaces ────────────────────────────────────────────
interface WalletAmountsState {
  loading: boolean;
  cashOutAmount: number;
  openedAmount: number;
  availableBalanceAmount: number;
  inProgressAmount: number;
  manualWithdrawEnabled: boolean;
  manualWithdrawLoading: boolean;
}

interface TotalWalletAmountsState {
  totalLoading: boolean;
  totalCashOutAmount: number;
  totalOpenedAmount: number;
  totalAvailableBalanceAmount: number;
  totalInProgressAmount: number;
}

interface TransactionState {
  transactions: Transaction[];
  transactionsLoading: boolean;
  transactionsError: string | null;
  page: number;
  totalPages: number;
  hasMore: boolean;
}

interface TotalTransactionState {
  totalTransactions: Transaction[];
  totalTransactionsLoading: boolean;
  totalTransactionsError: string | null;
  totalPage: number;
  totalTotalPages: number;
  totalHasMore: boolean;
}

interface WithdrawalState {
  withdrawalLoading: boolean;
  withdrawalError: string | null;
  withdrawalHistory: WithdrawalHistoryItem[];
  withdrawalHistoryLoading: boolean;
  withdrawalHistoryError: string | null;
  withdrawalHistoryPage: number;
  withdrawalHistoryTotalPages: number;
  withdrawalHistoryHasMore: boolean;
}

interface CommonState {
  organizationCode: string;
  activeTab: TabType;
}

// ─── Combined Store Interface ────────────────────────────────────
interface WalletStore
  extends WalletAmountsState,
    TotalWalletAmountsState,
    TransactionState,
    TotalTransactionState,
    WithdrawalState,
    CommonState {
  // Actions
  setWalletField: (
    fields: Partial<Omit<WalletStore, "setWalletField">>
  ) => void;
  loadInitialTransactions: (orgAdminId: string) => Promise<void>;
  loadMoreTransactions: (orgAdminId: string) => Promise<void>;
  loadInitialTotalTransactions: (orgAdminId: string) => Promise<void>;
  loadMoreTotalTransactions: (orgAdminId: string) => Promise<void>;
  createOrganizationWithdrawal: (
    beneficiaryId: string,
    amount: string,
    beneficiaryName: string
  ) => Promise<{ success: boolean }>;
  loadInitialOrganizationWithdrawalHistory: (orgCode: string) => Promise<void>;
  loadMoreOrganizationWithdrawalHistory: (orgCode: string) => Promise<void>;
  resetTransactionState: () => void;
  resetTotalTransactionState: () => void;
  resetWithdrawalHistoryState: () => void;
  setActiveTab: (tab: TabType) => Promise<void>;
  refreshAllData: (organizationCode: string) => Promise<void>;
  saveWalletState: () => void;
  handleScreenSizeChange: () => void;
}

// ─── Constants ─────────────────────────────────────────────────
const PAGE_SIZE = 10;
const SMALL_SCREEN_BREAKPOINT = 992;
const TABLET_WIDTH_THRESHOLD = 1000;
const TABLET_HEIGHT_THRESHOLD = 1900;

// ─── Initial States ─────────────────────────────────────────────
const initialWalletAmounts: WalletAmountsState = {
  loading: false,
  cashOutAmount: 0,
  openedAmount: 0,
  inProgressAmount: 0,
  availableBalanceAmount: 0,
  manualWithdrawEnabled: false,
  manualWithdrawLoading: false,
};

const initialTotalWalletAmounts: TotalWalletAmountsState = {
  totalLoading: false,
  totalCashOutAmount: 0,
  totalOpenedAmount: 0,
  totalInProgressAmount: 0,
  totalAvailableBalanceAmount: 0,
};

const initialTransactionState: TransactionState = {
  transactions: [],
  transactionsLoading: false,
  transactionsError: null,
  page: 0,
  totalPages: 1,
  hasMore: true,
};

const initialTotalTransactionState: TotalTransactionState = {
  totalTransactions: [],
  totalTransactionsLoading: false,
  totalTransactionsError: null,
  totalPage: 0,
  totalTotalPages: 1,
  totalHasMore: true,
};

const initialWithdrawalState: WithdrawalState = {
  withdrawalLoading: false,
  withdrawalError: null,
  withdrawalHistory: [],
  withdrawalHistoryLoading: false,
  withdrawalHistoryError: null,
  withdrawalHistoryPage: 0,
  withdrawalHistoryTotalPages: 1,
  withdrawalHistoryHasMore: true,
};

const initialCommonState: CommonState = {
  organizationCode: "",
  activeTab: "transaction",
};

// ─── Store Context ──────────────────────────────────────────────
const WalletContext = createContext<StoreApi<WalletStore> | null>(null);

// ─── Provider Component ──────────────────────────────────────────
export const WalletProvider = ({ children }: PropsWithChildren) => {
  const { showError, showSuccess } = useAlertStore();
  const { t } = useTranslation();
  const location = useLocation(); // Get current location for pathname tracking
  const userToken = AuthConfig.getToken();
  const organizationCode = useOrganizationCode(userToken) || "";

  // ─── Store Creation ─────────────────────────────────────────────
  const [store] = useState(() =>
    createStore<WalletStore>((set, get) => ({
      // Initial state
      ...initialWalletAmounts,
      ...initialTotalWalletAmounts,
      ...initialTransactionState,
      ...initialTotalTransactionState,
      ...initialWithdrawalState,
      ...initialCommonState,
      organizationCode,

      // ─── Generic Actions ──────────────────────────────────────────
      setWalletField: (fields) => set(fields),

      // ─── Screen Size Handler ────────────────────────────────────────
      handleScreenSizeChange: () => {
        const currentState = get();
        const orgAdminId = AuthConfig?.getUserInfo()?.userIdentification;
        const isSmallScreen =
          window.innerWidth < SMALL_SCREEN_BREAKPOINT ||
          (window.innerWidth >= TABLET_WIDTH_THRESHOLD &&
            window.innerHeight >= TABLET_HEIGHT_THRESHOLD);

        if (isSmallScreen && currentState.activeTab !== "transaction") {
          set({ activeTab: "transaction" });
          get().resetWithdrawalHistoryState();
          const isMoneyTransactionsRoute =
            location.pathname === ROUTES.MONEY_TRANSACTIONS;

          if (!isMoneyTransactionsRoute) {
            get().loadInitialTransactions(orgAdminId);
          } else {
            get().loadInitialTotalTransactions(currentState?.organizationCode);
          }
        }
      },

      // ─── Wallet Actions ───────────────────────────────────────────
      createOrganizationWithdrawal: async (
        beneficiaryId: string,
        amount: string,
        beneficiaryName: string
      ) => {
        set({ withdrawalLoading: true, withdrawalError: null });

        try {
          // Make API request
          await createOrganizationWithdrawalApi({
            beneficiaryId,
            amount,
            beneficiaryName,
          });

          // Show success message
          showSuccess(t("balance_bulk_withdrawal_success_title"));

          // Refresh data after successful withdrawal
          const orgAdminId = AuthConfig.getUserInfo()?.userIdentification;
          const orgCode = get().organizationCode;

          if (orgAdminId && orgCode) {
            // Update wallet amounts and total wallet amounts
            const [walletRes, totalWalletRes] = await Promise.all([
              getOrganizationWalletAmounts(orgAdminId),
              getTotalOrganizationWalletAmounts(orgCode),
            ]);

            if (walletRes?.data) {
              const {
                cashOutAmount,
                inProgressAmount,
                openedAmount,
                availableBalanceAmount,
              } = walletRes.data as WalletAmounts;

              set({
                cashOutAmount,
                inProgressAmount,
                openedAmount,
                availableBalanceAmount,
                withdrawalLoading: false,
              });
            }

            if (totalWalletRes?.data) {
              const {
                cashOutAmount: totalCashOutAmount,
                inProgressAmount: totalInProgressAmount,
                openedAmount: totalOpenedAmount,
                availableBalanceAmount: totalAvailableBalanceAmount,
              } = totalWalletRes.data as WalletAmounts;

              set({
                totalCashOutAmount,
                totalInProgressAmount,
                totalOpenedAmount,
                totalAvailableBalanceAmount,
              });
            }

            // Refresh withdrawal history
            await get().loadInitialOrganizationWithdrawalHistory(orgCode);
          }

          return { success: true };
        } catch (error: any) {
          showError(t("balance_error_title"));
          set({
            withdrawalLoading: false,
            withdrawalError: error?.message || "Withdrawal failed",
          });
          return { success: false };
        }
      },

      // ─── Transaction Actions ────────────────────────────────────────
      resetTransactionState: () => {
        set({
          ...initialTransactionState,
          transactionsLoading: true,
        });
      },

      loadInitialTransactions: async (userId: string) => {
        set({
          transactionsLoading: true,
          transactionsError: null,
          page: 0,
          hasMore: true,
        });

        try {
          const res = await getOrganizationWalletGroupingHistory(
            userId,
            PAGE_SIZE,
            0
          );
          const data = res?.data as PaginatedResponse<Transaction> | undefined;

          set({
            transactions: data?.content || [],
            page: 0,
            totalPages: data?.totalPages || 1,
            hasMore: (data?.page || 0) + 1 < (data?.totalPages || 1),
            transactionsLoading: false,
          });
        } catch (error: any) {
          showError(t("balance_error_title"));
          set({
            transactionsError: null,
            transactionsLoading: false,
          });
        }
      },

      loadMoreTransactions: async (userId: string) => {
        const { page, totalPages, transactions, transactionsLoading, hasMore } =
          get();

        if (transactionsLoading || !hasMore) return;

        set({
          transactionsLoading: true,
          transactionsError: null,
        });

        try {
          const nextPage = page + 1;
          const res = await getOrganizationWalletGroupingHistory(
            userId,
            PAGE_SIZE,
            nextPage
          );
          const data = res?.data as PaginatedResponse<Transaction> | undefined;

          set({
            transactions: [...transactions, ...(data?.content || [])],
            page: nextPage,
            totalPages: data?.totalPages || totalPages,
            hasMore:
              (data?.page || nextPage) + 1 < (data?.totalPages || totalPages),
            transactionsLoading: false,
          });
        } catch (error: any) {
          showError(t("balance_error_title"));
          set({
            transactionsError: null,
            transactionsLoading: false,
          });
        }
      },

      // ─── Total Transaction Actions ──────────────────────────────────
      resetTotalTransactionState: () => {
        set({
          ...initialTotalTransactionState,
          totalTransactionsLoading: true,
        });
      },

      loadInitialTotalTransactions: async (orgCode: string) => {
        set({
          totalTransactionsLoading: true,
          totalTransactionsError: null,
          totalPage: 0,
          totalHasMore: true,
        });

        try {
          const res = await getTotalOrganizationWalletGroupingHistory(
            orgCode,
            PAGE_SIZE,
            0
          );
          const data = res?.data as PaginatedResponse<Transaction> | undefined;

          set({
            totalTransactions: data?.content || [],
            totalPage: 0,
            totalTotalPages: data?.totalPages || 1,
            totalHasMore: (data?.page || 0) + 1 < (data?.totalPages || 1),
            totalTransactionsLoading: false,
          });
        } catch (error: any) {
          showError(t("balance_error_title"));
          set({
            totalTransactionsError: error?.error,
            totalTransactionsLoading: false,
          });
        }
      },

      loadMoreTotalTransactions: async (orgCode: string) => {
        const {
          totalPage,
          totalTotalPages,
          totalTransactions,
          totalTransactionsLoading,
          totalHasMore,
        } = get();

        if (totalTransactionsLoading || !totalHasMore) return;

        set({
          totalTransactionsLoading: true,
          totalTransactionsError: null,
        });

        try {
          const nextPage = totalPage + 1;
          const res = await getTotalOrganizationWalletGroupingHistory(
            orgCode,
            PAGE_SIZE,
            nextPage
          );
          const data = res?.data as PaginatedResponse<Transaction> | undefined;

          set({
            totalTransactions: [...totalTransactions, ...(data?.content || [])],
            totalPage: nextPage,
            totalTotalPages: data?.totalPages || totalTotalPages,
            totalHasMore:
              (data?.page || nextPage) + 1 <
              (data?.totalPages || totalTotalPages),
            totalTransactionsLoading: false,
          });
        } catch (error: any) {
          showError(t("balance_error_title"));
          set({
            totalTransactionsError: error?.error,
            totalTransactionsLoading: false,
          });
        }
      },

      // ─── Withdrawal History Actions ────────────────────────────────
      resetWithdrawalHistoryState: () => {
        set({
          ...initialWithdrawalState,
          withdrawalHistoryLoading: true,
        });
      },

      loadInitialOrganizationWithdrawalHistory: async (orgCode: string) => {
        set({
          withdrawalHistoryLoading: true,
          withdrawalHistoryError: null,
          withdrawalHistoryPage: 0,
          withdrawalHistoryHasMore: true,
        });

        try {
          const res = await getOrganizationWithdrawalHistory(
            orgCode,
            PAGE_SIZE,
            0
          );
          const data = res?.data as
            | PaginatedResponse<WithdrawalHistoryItem>
            | undefined;

          set({
            withdrawalHistory: data?.content || [],
            withdrawalHistoryPage: 0,
            withdrawalHistoryTotalPages: data?.totalPages || 1,
            withdrawalHistoryHasMore:
              (data?.pageable?.pageNumber || 0) + 1 < (data?.totalPages || 1),
            withdrawalHistoryLoading: false,
          });
        } catch (error: any) {
          showError(t("balance_error_title"));
          console.log(error);
          set({
            withdrawalHistoryError: error?.error,
            withdrawalHistoryLoading: false,
          });
        }
      },

      loadMoreOrganizationWithdrawalHistory: async (orgCode: string) => {
        const {
          withdrawalHistoryPage,
          withdrawalHistoryTotalPages,
          withdrawalHistory,
          withdrawalHistoryLoading,
          withdrawalHistoryHasMore,
        } = get();

        if (withdrawalHistoryLoading || !withdrawalHistoryHasMore) return;

        set({
          withdrawalHistoryLoading: true,
          withdrawalHistoryError: null,
        });

        try {
          const nextPage = withdrawalHistoryPage + 1;
          const res = await getOrganizationWithdrawalHistory(
            orgCode,
            PAGE_SIZE,
            nextPage
          );
          const data = res?.data as
            | PaginatedResponse<WithdrawalHistoryItem>
            | undefined;

          set({
            withdrawalHistory: [...withdrawalHistory, ...(data?.content || [])],
            withdrawalHistoryPage: nextPage,
            withdrawalHistoryTotalPages:
              data?.totalPages || withdrawalHistoryTotalPages,
            withdrawalHistoryHasMore:
              (data?.pageable?.pageNumber || nextPage) + 1 <
              (data?.totalPages || withdrawalHistoryTotalPages),
            withdrawalHistoryLoading: false,
          });
        } catch (error: any) {
          showError(t("balance_error_title"));
          set({
            withdrawalHistoryError: error?.error,
            withdrawalHistoryLoading: false,
          });
        }
      },

      // ─── Tab Management ───────────────────────────────────────────
      setActiveTab: async (tab: TabType) => {
        set({ activeTab: tab });
        const orgCode = get().organizationCode;
        const orgAdminId = AuthConfig?.getUserInfo()?.userIdentification;
        const isMoneyTransactionsRoute =
          location.pathname === ROUTES.MONEY_TRANSACTIONS;

        if (!orgCode) return;

        if (tab === "transaction") {
          get().resetWithdrawalHistoryState();
          if (isMoneyTransactionsRoute)
            await get().loadInitialTotalTransactions(orgCode);
          else await get().loadInitialTransactions(orgAdminId);
        } else {
          get().resetTransactionState();
          await get().loadInitialOrganizationWithdrawalHistory(orgCode);
        }

        // Update wallet amounts regardless of tab
        set({ loading: true, totalLoading: true });
        try {
          const [walletRes, totalWalletRes] = await Promise.all([
            getOrganizationWalletAmounts(orgAdminId),
            getTotalOrganizationWalletAmounts(orgCode),
          ]);

          if (walletRes?.data) {
            const {
              cashOutAmount,
              inProgressAmount,
              openedAmount,
              availableBalanceAmount,
            } = walletRes.data;

            set({
              cashOutAmount,
              inProgressAmount,
              openedAmount,
              availableBalanceAmount,
              loading: false,
            });
          }

          if (totalWalletRes?.data) {
            const {
              cashOutAmount: totalCashOutAmount,
              inProgressAmount: totalInProgressAmount,
              openedAmount: totalOpenedAmount,
              availableBalanceAmount: totalAvailableBalanceAmount,
            } = totalWalletRes.data;

            set({
              totalCashOutAmount,
              totalInProgressAmount,
              totalOpenedAmount,
              totalAvailableBalanceAmount,
              totalLoading: false,
            });
          }
        } catch (error) {
          console.error("Error loading wallet amounts:", error);
          set({ loading: false, totalLoading: false });
        }
      },

      // ─── Data Management ──────────────────────────────────────────
      refreshAllData: async (userId: string) => {
        get().resetTransactionState();
        get().resetTotalTransactionState();
        get().resetWithdrawalHistoryState();

        await Promise.all([
          get().loadInitialTransactions(userId),
          get().loadInitialTotalTransactions(organizationCode),
          get().loadInitialOrganizationWithdrawalHistory(organizationCode),
        ]);
      },

      saveWalletState: () => {
        const state = get();
        const stateToSave = {
          // Transaction data
          transactions: state.transactions,
          page: state.page,
          totalPages: state.totalPages,
          hasMore: state.hasMore,

          // Total transaction data
          totalTransactions: state.totalTransactions,
          totalPage: state.totalPage,
          totalTotalPages: state.totalTotalPages,
          totalHasMore: state.totalHasMore,

          // Wallet amounts
          cashOutAmount: state.cashOutAmount,
          openedAmount: state.openedAmount,
          inProgressAmount: state.inProgressAmount,
          availableBalanceAmount: state.availableBalanceAmount,
          manualWithdrawEnabled: state.manualWithdrawEnabled,
          organizationCode: state.organizationCode,

          // Total wallet amounts
          totalCashOutAmount: state.totalCashOutAmount,
          totalOpenedAmount: state.totalOpenedAmount,
          totalInProgressAmount: state.totalInProgressAmount,
          totalAvailableBalanceAmount: state.totalAvailableBalanceAmount,

          // Withdrawal history
          withdrawalHistory: state.withdrawalHistory,
          withdrawalHistoryPage: state.withdrawalHistoryPage,
          withdrawalHistoryTotalPages: state.withdrawalHistoryTotalPages,
          withdrawalHistoryHasMore: state.withdrawalHistoryHasMore,

          // Active tab
          activeTab: state.activeTab,
        };

        sessionStorage.setItem("walletState", JSON.stringify(stateToSave));
      },
    }))
  );

  // ─── Initialize Wallet Data ────────────────────────────────────
  useEffect(() => {
    const fetchWallet = async () => {
      if (!organizationCode) return;

      // Check if returning from contract page
      const previousPath = sessionStorage.getItem("previousPath");
      const isReturningFromContract = previousPath?.includes(
        "/payments/contract/"
      );

      // Attempt to restore state from session storage
      if (isReturningFromContract) {
        const savedState = sessionStorage.getItem("walletState");
        if (savedState) {
          try {
            const parsedState = JSON.parse(savedState);
            store.getState().setWalletField({
              ...parsedState,
              transactionsLoading: false,
              transactionsError: null,
              manualWithdrawLoading: false,
              withdrawalHistoryLoading: false,
              withdrawalHistoryError: null,
            });
            return; // Skip API calls if state restored successfully
          } catch (error) {
            console.error("Error restoring wallet state:", error);
          }
        }
      } else {
        // Clear session storage if not returning from contract
        sessionStorage.removeItem("walletState");
        sessionStorage.removeItem("walletScrollPosition");
        sessionStorage.removeItem("previousPath");
      }

      // Check current route to determine which data to load
      const isMoneyTransactionsRoute =
        location.pathname === ROUTES.MONEY_TRANSACTIONS;

      // Check if wallet data is already loaded
      const currentState = store.getState();
      if (
        (!isMoneyTransactionsRoute &&
          currentState.transactions.length > 0 &&
          !currentState.loading) ||
        (isMoneyTransactionsRoute &&
          currentState.totalTransactions.length > 0 &&
          !currentState.totalLoading)
      ) {
        return; // Skip API calls if data already loaded
      }

      // Fetch wallet data
      store.getState().setWalletField({
        loading: !isMoneyTransactionsRoute,
        totalLoading: isMoneyTransactionsRoute,
      });

      const orgAdminId = AuthConfig?.getUserInfo()?.userIdentification;
      try {
        if (isMoneyTransactionsRoute) {
          // Load total wallet amounts for money transactions route
          const totalWalletRes = await getTotalOrganizationWalletAmounts(
            organizationCode
          );

          if (totalWalletRes?.data) {
            const {
              cashOutAmount: totalCashOutAmount,
              inProgressAmount: totalInProgressAmount,
              openedAmount: totalOpenedAmount,
              availableBalanceAmount: totalAvailableBalanceAmount,
            } = totalWalletRes.data;

            store.getState().setWalletField({
              totalCashOutAmount,
              totalInProgressAmount,
              totalOpenedAmount,
              totalAvailableBalanceAmount,
              organizationCode,
              totalLoading: false,
            });
          }
        } else {
          // Load regular wallet amounts for other routes
          const walletRes = await getOrganizationWalletAmounts(orgAdminId);

          if (walletRes?.data) {
            const {
              cashOutAmount,
              inProgressAmount,
              openedAmount,
              availableBalanceAmount,
            } = walletRes.data;

            store.getState().setWalletField({
              cashOutAmount,
              inProgressAmount,
              openedAmount,
              availableBalanceAmount,
              organizationCode,
              loading: false,
            });
          }
        }
      } catch (error) {
        console.error("Error loading wallet:", error);
        showError(t("balance_error_title"));
        store.getState().setWalletField({
          loading: !isMoneyTransactionsRoute,
          totalLoading: isMoneyTransactionsRoute,
        }); // Keep loading state to show shimmer
      }
    };

    // Initialize wallet data
    fetchWallet();

    const orgAdminId = AuthConfig?.getUserInfo()?.userIdentification;
    const isMoneyTransactionsRoute =
      window.location.pathname === ROUTES.MONEY_TRANSACTIONS;

    // Load initial transactions based on route
    if (isMoneyTransactionsRoute) {
      // Load total transactions for money transactions route
      if (organizationCode && !store.getState().totalTransactions.length) {
        store.getState().setWalletField({ organizationCode });
        store.getState().loadInitialTotalTransactions(organizationCode);
      }
    } else {
      // Load regular transactions for other routes
      if (organizationCode && !store.getState().transactions.length) {
        store.getState().setWalletField({ organizationCode });
        store.getState().loadInitialTransactions(orgAdminId);
      }
      // Load initial withdrawal history if needed (for all routes)
      if (organizationCode && !store.getState().withdrawalHistory.length) {
        store
          .getState()
          .loadInitialOrganizationWithdrawalHistory(organizationCode);
      }
    }

    // Check screen size and set appropriate tab
    store.getState().handleScreenSizeChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationCode, location.pathname]); // React to pathname changes

  // ─── Bank Status Alert ─────────────────────────────────────────
  useEffect(() => {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const bankStatus = searchParams.get("bankStatus");

      if (bankStatus === "added") {
        showSuccess(t("lbl_bank_account_added_success_msg"));
      }
    } catch (error) {
      // Silently fail on error parsing URL parameters
    }
  }, [t, showSuccess]);

  // ─── Screen Size Event Listener ─────────────────────────────────
  useEffect(() => {
    const handleResize = () => store.getState().handleScreenSizeChange();

    window.addEventListener("resize", handleResize);
    handleResize(); // Initial check

    return () => window.removeEventListener("resize", handleResize);
  }, [store]);

  return (
    <WalletContext.Provider value={store}>{children}</WalletContext.Provider>
  );
};

// ─── Custom Hook ──────────────────────────────────────────────────
export const useWallet = <T,>(selector: (state: WalletStore) => T): T => {
  const context = useContext(WalletContext);
  if (!context) throw new Error("useWallet must be used within WalletProvider");
  return useStore(context, selector);
};
